import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, PhoneCall, Camera, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Seal } from "@/components/Seal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_NAMES = {
    starter_savv: "Starter Savv",
    social_savv: "Social Savv",
    brand_savv: "Brand Savv",
    full_savv: "Full Savv",
};

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "loading", lookup: null });
    const tries = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", lookup: null });
            return;
        }
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid", lookup: data.lookup_key });
                    return;
                }
            } catch {
                /* keep polling */
            }
            tries.current += 1;
            if (tries.current < 15) {
                setTimeout(poll, 2500);
            } else {
                setState({ status: "pending", lookup: null });
            }
        };
        poll();
    }, [sessionId]);

    return (
        <div
            data-testid="payment-success-page"
            className="min-h-screen flex items-center justify-center px-5 py-16"
        >
            <div className="w-full max-w-xl bg-[#101010] border border-[#262626] rounded-[2rem] p-10 md:p-14 text-center shadow-[0_25px_60px_-25px_rgba(0,0,0,0.6)]">
                <div className="flex justify-center mb-8">
                    <Logo />
                </div>
                {state.status === "loading" && (
                    <div data-testid="payment-status-pending">
                        <Loader2 size={32} className="mx-auto animate-spin text-[#D3A0A7]" />
                        <h1 className="mt-6 font-serif-ed text-3xl text-[#F1F1EF]">
                            Confirming your payment…
                        </h1>
                        <p className="mt-3 text-[#A9A6A0] text-sm">
                            One moment while Stripe lets us know.
                        </p>
                    </div>
                )}
                {state.status === "paid" && (
                    <div data-testid="payment-status-paid">
                        <Seal />
                        <h1 className="mt-8 font-serif-ed text-3xl md:text-4xl text-[#F1F1EF]">
                            {PACKAGE_NAMES[state.lookup] || "Your package"} is locked in.
                        </h1>
                        <p className="mt-3 font-hand text-2xl text-[#D3A0A7]">
                            sealed, paid, unforgettable ♡
                        </p>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                            <span className="inline-flex items-center gap-2 bg-[#161616] text-[#F1F1EF] text-xs font-semibold px-4 py-2 rounded-full border border-[#CFCCC6]/50">
                                <PhoneCall size={14} /> Free strategy call
                            </span>
                            <span className="inline-flex items-center gap-2 bg-[#EAF1F7] text-[#D3A0A7] text-xs font-semibold px-4 py-2 rounded-full border border-[#C9DDEE]/60">
                                <Camera size={14} /> Free brand photo shoot
                            </span>
                        </div>
                        <p className="mt-6 text-[#A9A6A0] text-sm leading-relaxed">
                            Tabbie has been emailed. Now grab the time that suits
                            you:
                        </p>
                        <a
                            href="/#contact"
                            data-testid="payment-book-call-link"
                            className="group mt-8 inline-flex items-center gap-2 bg-[#0C0C0C] text-[#F1F1EF] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#D3A0A7] transition-colors duration-300"
                        >
                            Book Your Free Call
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                    </div>
                )}
                {(state.status === "pending" || state.status === "error") && (
                    <div data-testid="payment-status-processing">
                        <h1 className="font-serif-ed text-3xl text-[#F1F1EF]">
                            We&apos;re still confirming it.
                        </h1>
                        <p className="mt-3 text-[#A9A6A0] text-sm leading-relaxed">
                            Payments can take a minute to land. You&apos;ll get an
                            email as soon as it&apos;s confirmed — or reach us at
                            savvmedia.info@gmail.com.
                        </p>
                        <a
                            href="/"
                            data-testid="payment-home-link"
                            className="mt-8 inline-block text-sm font-semibold text-[#D3A0A7] link-underline"
                        >
                            Back to the site
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
